import { useAuth, useClerk } from "@clerk/clerk-react";
import { useCallback } from "react";

const BASE = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8787";

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

/**
 * Returns an authenticated fetch wrapper that injects the Clerk session JWT.
 * On 401, retries once with a freshly-minted token (handles in-flight expiry).
 * If the retry still 401s, the session is genuinely gone — redirects to
 * Clerk sign-in and throws so callers can surface a calm error state.
 */
export function useApi() {
  const { getToken } = useAuth();
  const { redirectToSignIn } = useClerk();

  const call = useCallback(
    async <T = unknown>(path: string, init: RequestInit = {}): Promise<T> => {
      const doFetch = async (skipCache: boolean) => {
        const token = await getToken(skipCache ? { skipCache: true } : undefined);
        return fetch(`${BASE}${path}`, {
          ...init,
          headers: {
            "content-type": "application/json",
            ...(token ? { authorization: `Bearer ${token}` } : {}),
            ...(init.headers ?? {}),
          },
        });
      };

      let res = await doFetch(false);
      if (res.status === 401) {
        res = await doFetch(true);
        if (res.status === 401) {
          redirectToSignIn();
          throw new ApiError(401, "session expired");
        }
      }

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new ApiError(res.status, txt || res.statusText);
      }
      if (res.status === 204) return undefined as T;
      return (await res.json()) as T;
    },
    [getToken, redirectToSignIn],
  );

  return {
    get: <T>(path: string) => call<T>(path),
    post: <T>(path: string, body?: unknown) =>
      call<T>(path, { method: "POST", body: body ? JSON.stringify(body) : undefined }),
    patch: <T>(path: string, body?: unknown) =>
      call<T>(path, { method: "PATCH", body: body ? JSON.stringify(body) : undefined }),
    del: <T>(path: string) => call<T>(path, { method: "DELETE" }),
  };
}
